import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { Loader2 } from 'lucide-react';
import { ThemeProvider } from './lib/theme';
import { ToastProvider } from './components/ui/toast';
import { logPageView } from './lib/analytics';
import { AppLayout } from './components/layout/app-layout';
import LoginPage from './pages/auth/login';

// Route-level code splitting — everything past the login page was
// previously in one JS bundle, so viewing the dashboard downloaded the
// super-admin portal and load-tester too. Each of these becomes its own
// chunk, fetched only when its route is actually visited.
const DashboardPage = lazy(() => import('./pages/dashboard'));
const UsersPage = lazy(() => import('./pages/users'));
const PaymentsPage = lazy(() => import('./pages/payments'));
const RefundsPage = lazy(() => import('./pages/refunds'));
const TokenizationPage = lazy(() => import('./pages/tokenization'));
const MandatesPage = lazy(() => import('./pages/mandates'));
const AnalyticsPage = lazy(() => import('./pages/analytics'));
const RoutingPage = lazy(() => import('./pages/routing'));
const RoutingBuilderPage = lazy(() => import('./pages/routing/builder'));
const LoadTesterPage = lazy(() => import('./pages/load-tester'));
const GatewayConfigPage = lazy(() => import('./pages/gateway-config'));
const PaymentConfigPage = lazy(() => import('./pages/payment-config'));
const SuperAdminLogin = lazy(() => import('./pages/super-admin/login'));
const SuperAdminPortal = lazy(() => import('./pages/super-admin'));
const OrderPrintPage = lazy(() => import('./pages/payments/order-print'));
const RefundPrintPage = lazy(() => import('./pages/refunds/refund-print'));
const SettingsPage = lazy(() => import('./pages/settings'));
const TwoFactorPage = lazy(() => import('./pages/settings/two-factor'));
const PasskeysPage = lazy(() => import('./pages/settings/passkeys'));
const TwoFactorResetPage = lazy(() => import('./pages/auth/two-factor-reset'));
const PasswordResetPage = lazy(() => import('./pages/auth/password-reset'));
const ImpersonateBootstrapPage = lazy(() => import('./pages/auth/impersonate-bootstrap'));
const CheckoutPage = lazy(() => import('./pages/checkout'));
const ReconPage = lazy(() => import('./pages/recon'));

function RouteFallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg, #050c18)' }}>
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#22d3ee' }} />
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    logPageView(location.pathname);
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: location.pathname,
      level: 'info',
    });
  }, [location.pathname]);
  return null;
}

export default function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{ padding: 40, fontFamily: 'sans-serif', background: '#050c18', minHeight: '100vh', color: '#f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          <h2 style={{ color: '#ef4444' }}>Something went wrong</h2>
          <pre style={{ color: '#94a3b8', fontSize: 12, maxWidth: 600, whiteSpace: 'pre-wrap' }}>{String(error)}</pre>
          <button onClick={resetError} style={{ padding: '8px 20px', background: '#22d3ee', color: '#000', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
            Try again
          </button>
        </div>
      )}
    >
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <BrowserRouter>
              <RouteTracker />
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/auth/2fa-reset" element={<TwoFactorResetPage />} />
                <Route path="/auth/password-reset" element={<PasswordResetPage />} />
                <Route path="/impersonate" element={<ImpersonateBootstrapPage />} />
                <Route path="/super-admin/login" element={<SuperAdminLogin />} />
                <Route path="/super-admin" element={<SuperAdminPortal />} />
                <Route element={<AppLayout />}>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/payments" element={<PaymentsPage />} />
                  <Route path="/refunds" element={<RefundsPage />} />
                  <Route path="/recon" element={<ReconPage />} />
                  <Route path="/tokenization" element={<TokenizationPage />} />
                  <Route path="/mandates" element={<MandatesPage />} />
                  <Route path="/analytics" element={<AnalyticsPage />} />
                  <Route path="/routing" element={<RoutingPage />} />
                  <Route path="/routing/:id" element={<RoutingBuilderPage />} />
                  {!import.meta.env.PROD && <Route path="/load-tester" element={<LoadTesterPage />} />}
                  <Route path="/gateway-config" element={<GatewayConfigPage />} />
                  <Route path="/payment-config"  element={<PaymentConfigPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/settings/2fa"   element={<TwoFactorPage />} />
                  <Route path="/settings/passkeys" element={<PasskeysPage />} />
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                </Route>
                <Route path="/payments/order/:id" element={<OrderPrintPage />} />
                <Route path="/refunds/:id" element={<RefundPrintPage />} />
                <Route path="/pay/:referenceId" element={<CheckoutPage />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
              </Suspense>
            </BrowserRouter>
          </ToastProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
}
